import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AUTH_SERVICE } from '@dibsondance/shared-authentication-base';
import { retryWithBackoff } from './retry-with-backoff';

/**
 * HTTP interceptor for DOD-Host application.
 *
 * This interceptor:
 * - Adds Firebase ID token as Bearer token to API requests
 * - Handles 429 Too Many Requests with exponential backoff retry
 * - Handles 401 Unauthorized responses by redirecting to login
 *
 * Features:
 * - Gets ID token from Firebase auth service
 * - Adds Authorization: Bearer <idToken> header
 * - Backend validates the ID token directly
 * - Retries 429 errors with exponential backoff (1s, 2s, 4s, 8s...)
 * - On 401, signs out user and redirects to login page
 *
 * No session cookies, no SSR server dependency, no proxy conversion needed.
 */
export const hostAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AUTH_SERVICE);
  const router = inject(Router);

  // Only process API requests to backend
  if (!req.url.includes('/api/')) {
    return next(req);
  }

  // Get current ID token from auth service
  const idToken = authService.idToken();

  const handle401Error = async () => {
    // Sign out user to clear auth state
    await authService.signOut();

    // Navigate to login page
    await router.navigate(['/login']);
  };

  const handle429Error = () => {
    // Don't navigate away, the retry logic will handle it
  };

  if (idToken) {
    // Clone request with Authorization header
    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return next(authReq).pipe(
      // Retry 429 errors with exponential backoff
      retryWithBackoff({
        maxRetries: 3,
        initialDelay: 2000, // Start with 2 seconds
        maxDelay: 30000, // Cap at 30 seconds
        shouldRetry: (error) => error.status === 429, // Only retry rate limiting
      }),
      // Success logging
      tap(() => {
        // Request succeeded (useful for debugging retry success)
      }),
      // Handle errors after all retries exhausted
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          handle401Error();
        } else if (error.status === 429) {
          handle429Error();
        }
        return throwError(() => error);
      })
    );
  } else {
    return next(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          handle401Error();
        } else if (error.status === 429) {
          handle429Error();
        }
        return throwError(() => error);
      })
    );
  }
};
